import type { KeyboardEvent, RefObject } from 'react';
import { CARD_CLASS } from '../browserStyles';
import { fullscreenKeyIndex } from './galleryFullscreen';
import { GalleryFullscreenToggle } from './GalleryFullscreenToggle';
import { ScanImagesPanel } from './ScanImagesPanel';
import { ScanImageTabList } from './ScanImageTabList';
import type { ScanGalleryState } from './useScanGallery';

const INLINE_CLASS = `${CARD_CLASS} flex flex-col gap-2.5 p-2.5`;
const FULLSCREEN_CLASS = `${CARD_CLASS} flex min-h-0 flex-1 flex-col gap-3 p-3 focus:outline-none`;

interface ScanGalleryProps {
    gallery: ScanGalleryState;
    // Both copies are mounted while full screen is open, so each needs its own element ids.
    idBase: string;
    fullscreen: boolean;
    rootRef?: RefObject<HTMLElement>;
    toggleRef?: RefObject<HTMLButtonElement>;
    onToggleFullscreen: () => void;
}

export function ScanGallery({
    gallery,
    idBase,
    fullscreen,
    rootRef,
    toggleRef,
    onToggleFullscreen
}: ScanGalleryProps) {
    const { segment, set, urls, index, count } = gallery;

    const handleFullscreenKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        const next = fullscreenKeyIndex(event.key, event.defaultPrevented, index, count);
        if (next === null) return;
        event.preventDefault();
        gallery.setIndex(next);
    };

    return (
        <section
            ref={rootRef}
            aria-label="Scan images"
            // Focusable in full screen so the arrow keys work from the start and after a click on the card.
            tabIndex={fullscreen ? -1 : undefined}
            onKeyDown={fullscreen ? handleFullscreenKeyDown : undefined}
            className={fullscreen ? FULLSCREEN_CLASS : INLINE_CLASS}
        >
            <div className="flex items-center justify-between gap-2">
                <ScanImageTabList
                    baseId={idBase}
                    activeTab={segment}
                    counts={gallery.counts}
                    onChange={gallery.selectSegment}
                />
                <GalleryFullscreenToggle
                    buttonRef={toggleRef}
                    fullscreen={fullscreen}
                    disabled={count === 0}
                    label={set.tab.label}
                    onToggle={onToggleFullscreen}
                />
            </div>
            <div
                id={`${idBase}-panel`}
                role="tabpanel"
                aria-labelledby={`${idBase}-tab-${segment}`}
                className={fullscreen ? 'flex min-h-0 flex-1 flex-col' : undefined}
            >
                <ScanImagesPanel
                    set={set}
                    urls={urls}
                    index={index}
                    fullscreen={fullscreen}
                    onIndexChange={gallery.setIndex}
                    onEnlarge={onToggleFullscreen}
                />
            </div>
        </section>
    );
}
